import React, {useEffect, useState} from 'react';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import {notifyError, notifySuccess} from '../../../utils/notifications';
import {apiMessage, DAY_NAMES, formatTerm, todayIso} from './inquiryUtils';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const inputClass = 'rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

/** Nastavení rezervací z webu: kdo dostává upozornění, otevírací doba, počet lidí, výjimky, pravidla. */
const WebSettings = () => {
    const [settings, setSettings] = useState(null);
    const [newEmail, setNewEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        API.get(API_ENDPOINTS.webInquiry.settings)
            .then(({data}) => setSettings({
                ...data,
                // minulé výjimky už nejsou potřeba
                dayOverrides: (data.dayOverrides || []).filter((item) => item.date >= todayIso())
            }))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    if (!settings) return <div className="p-4 text-sm text-gray-500">Načítám nastavení…</div>;

    const update = (patch) => {
        setSettings((prev) => ({...prev, ...patch}));
        setDirty(true);
    };
    const updateDay = (day, patch) => update({
        openingHours: settings.openingHours.map((item) => (item.day === day ? {...item, ...patch} : item))
    });
    const updateOverride = (index, patch) => update({
        dayOverrides: settings.dayOverrides.map((item, i) => (i === index ? {...item, ...patch} : item))
    });

    const addEmail = () => {
        const email = newEmail.trim().toLowerCase();
        if (!EMAIL_RE.test(email)) {
            notifyError('Zadejte platný e-mail.');
            return;
        }
        if (!settings.notifyEmails.includes(email)) update({notifyEmails: [...settings.notifyEmails, email]});
        setNewEmail('');
    };

    const save = async () => {
        const invalidDay = settings.openingHours.find((item) => !item.closed && item.open >= item.close);
        if (invalidDay) {
            notifyError(`${DAY_NAMES[invalidDay.day]}: čas otevření musí být dřív než zavření.`);
            return;
        }
        const reviewUrl = (settings.reviewRequest?.url || '').trim();
        if (reviewUrl && !/^https?:\/\//.test(reviewUrl)) {
            notifyError('Odkaz na hodnocení musí začínat https://');
            return;
        }
        const overrides = settings.dayOverrides.filter((item) => item.date);
        setSaving(true);
        try {
            await API.put(API_ENDPOINTS.appSettings.update, {
                web: {
                    notifyEmails: settings.notifyEmails,
                    openingHours: settings.openingHours.map((item) => ({
                        ...item,
                        technicians: item.closed ? 0 : Math.max(0, Number(item.technicians) || 0)
                    })),
                    dayOverrides: overrides
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map((item) => ({
                            date: item.date,
                            closed: Boolean(item.closed),
                            ...(item.closed ? {} : {
                                open: item.open || undefined,
                                close: item.close || undefined,
                                technicians: item.technicians === '' || item.technicians == null ? undefined : Number(item.technicians)
                            }),
                            note: item.note || ''
                        })),
                    slotMinutes: Number(settings.slotMinutes),
                    leadMinutes: Number(settings.leadMinutes),
                    horizonDays: Number(settings.horizonDays),
                    defaultRepairMinutes: Number(settings.defaultRepairMinutes),
                    reminderEnabled: Boolean(settings.reminderEnabled),
                    reminderHour: Number(settings.reminderHour),
                    reviewRequest: {
                        enabled: Boolean(settings.reviewRequest?.enabled),
                        url: (settings.reviewRequest?.url || '').trim(),
                        delayDays: Number(settings.reviewRequest?.delayDays) || 0,
                        ...(settings.reviewRequest?.enabledAt ? {enabledAt: settings.reviewRequest.enabledAt} : {})
                    },
                    contact: settings.contact
                }
            }, {meta: {silentSuccess: true, silentError: true}});
            setDirty(false);
            notifySuccess('Nastavení uloženo');
        } catch (e) {
            notifyError(apiMessage(e, 'Nastavení se nepodařilo uložit.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mx-auto max-w-4xl space-y-4 pb-24">
            <div>
                <h1 className="text-xl font-semibold">Rezervace a web</h1>
                <p className="text-sm text-gray-600">Nastavení pro rezervace, kontaktní formuláře a poptávky z applelab.cz.</p>
            </div>

            <Card title="Upozornění na nové požadavky" description="Na tyto adresy přijde e-mail o každé nové rezervaci, zprávě a poptávce z webu.">
                {settings.notifyEmails.length ? (
                    <ul className="mb-3 flex flex-wrap gap-2">
                        {settings.notifyEmails.map((email) => (
                            <li key={email} className="flex items-center gap-1 rounded-full bg-gray-100 py-1 pl-3 pr-1 text-sm">
                                {email}
                                <button
                                    type="button"
                                    onClick={() => update({notifyEmails: settings.notifyEmails.filter((item) => item !== email)})}
                                    className="rounded-full px-2 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                                    aria-label={`Odebrat ${email}`}
                                >
                                    ×
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="mb-3 rounded-md bg-amber-50 p-2 text-sm text-amber-800">Zatím žádný e-mail – o nových požadavcích se dozvíte jen v administraci.</p>
                )}
                <div className="flex gap-2">
                    <input
                        type="email"
                        value={newEmail}
                        onChange={(event) => setNewEmail(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                addEmail();
                            }
                        }}
                        placeholder="servis@applelab.cz"
                        className={`${inputClass} w-72`}
                    />
                    <button type="button" onClick={addEmail} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">
                        Přidat
                    </button>
                </div>
            </Card>

            <Card
                title="Otevírací doba a lidé na servisu"
                description="Počet lidí určuje, kolik zákazníků může přijít ve stejný čas a kolik práce se za den stihne (lidé × otevírací doba). Otevírací doba se zobrazuje i na webu."
            >
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[28rem] text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500">
                                <th className="py-1 font-normal">Den</th>
                                <th className="py-1 font-normal">Otevřeno</th>
                                <th className="py-1 font-normal">Od</th>
                                <th className="py-1 font-normal">Do</th>
                                <th className="py-1 font-normal">Lidí na servisu</th>
                            </tr>
                        </thead>
                        <tbody>
                            {settings.openingHours.map((item) => (
                                <tr key={item.day} className="border-t border-gray-100">
                                    <td className="py-1.5 pr-3 font-medium">{DAY_NAMES[item.day]}</td>
                                    <td className="py-1.5 pr-3">
                                        <input
                                            type="checkbox"
                                            checked={!item.closed}
                                            onChange={(event) => updateDay(item.day, {
                                                closed: !event.target.checked,
                                                technicians: event.target.checked && !item.technicians ? 1 : item.technicians
                                            })}
                                            aria-label={`${DAY_NAMES[item.day]} otevřeno`}
                                        />
                                    </td>
                                    <td className="py-1.5 pr-3">
                                        <input type="time" step={900} disabled={item.closed} value={item.open} onChange={(event) => updateDay(item.day, {open: event.target.value})} className={inputClass}/>
                                    </td>
                                    <td className="py-1.5 pr-3">
                                        <input type="time" step={900} disabled={item.closed} value={item.close} onChange={(event) => updateDay(item.day, {close: event.target.value})} className={inputClass}/>
                                    </td>
                                    <td className="py-1.5">
                                        <input type="number" min={0} max={20} disabled={item.closed} value={item.closed ? 0 : item.technicians} onChange={(event) => updateDay(item.day, {technicians: event.target.value})} className={`${inputClass} w-20`}/>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Card
                title="Výjimky – svátky, dovolená, jiný počet lidí"
                description="Pro konkrétní den přepíše otevírací dobu nebo počet lidí. Prázdné pole = beze změny."
            >
                {settings.dayOverrides.length ? (
                    <ul className="mb-3 space-y-2">
                        {settings.dayOverrides.map((item, index) => (
                            <li key={index} className="flex flex-wrap items-end gap-2 rounded-md border border-gray-200 p-2 text-sm">
                                <label className="flex flex-col text-xs text-gray-500">
                                    Datum
                                    <input type="date" min={todayIso()} value={item.date} onChange={(event) => updateOverride(index, {date: event.target.value})} className={inputClass}/>
                                </label>
                                <label className="flex items-center gap-1.5 pb-2">
                                    <input type="checkbox" checked={Boolean(item.closed)} onChange={(event) => updateOverride(index, {closed: event.target.checked})}/>
                                    Zavřeno
                                </label>
                                {!item.closed ? (
                                    <>
                                        <label className="flex flex-col text-xs text-gray-500">
                                            Od
                                            <input type="time" step={900} value={item.open || ''} onChange={(event) => updateOverride(index, {open: event.target.value})} className={inputClass}/>
                                        </label>
                                        <label className="flex flex-col text-xs text-gray-500">
                                            Do
                                            <input type="time" step={900} value={item.close || ''} onChange={(event) => updateOverride(index, {close: event.target.value})} className={inputClass}/>
                                        </label>
                                        <label className="flex flex-col text-xs text-gray-500">
                                            Lidí
                                            <input type="number" min={0} max={20} value={item.technicians ?? ''} onChange={(event) => updateOverride(index, {technicians: event.target.value})} className={`${inputClass} w-20`}/>
                                        </label>
                                    </>
                                ) : null}
                                <label className="flex min-w-[10rem] flex-1 flex-col text-xs text-gray-500">
                                    Poznámka (zobrazí se v kalendáři)
                                    <input value={item.note || ''} placeholder="Např. Státní svátek" onChange={(event) => updateOverride(index, {note: event.target.value})} className={inputClass}/>
                                </label>
                                <span className="pb-2 text-xs text-gray-500">{item.date ? formatTerm(item.date) : ''}</span>
                                <button
                                    type="button"
                                    onClick={() => update({dayOverrides: settings.dayOverrides.filter((_, i) => i !== index)})}
                                    className="mb-1 rounded px-2 py-1 text-red-600 hover:bg-red-50"
                                >
                                    Odebrat
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : null}
                <button
                    type="button"
                    onClick={() => update({dayOverrides: [...settings.dayOverrides, {date: '', closed: true, note: ''}]})}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                >
                    + Přidat výjimku
                </button>
            </Card>

            <Card title="Pravidla rezervací">
                <div className="grid gap-4 sm:grid-cols-2">
                    <NumberField label="Interval termínů (min)" hint="Po kolika minutách se nabízejí časy předání." value={settings.slotMinutes} onChange={(value) => update({slotMinutes: value})} min={10} max={120} step={5}/>
                    <NumberField label="Nejbližší termín za (min)" hint="Dnešní termíny se nabízejí nejdřív za tuto dobu." value={settings.leadMinutes} onChange={(value) => update({leadMinutes: value})} min={0} step={15}/>
                    <NumberField label="Rezervace dopředu (dní)" hint="Na kolik dní dopředu web nabízí termíny." value={settings.horizonDays} onChange={(value) => update({horizonDays: value})} min={1} max={60}/>
                    <NumberField
                        label="Výchozí náročnost opravy (min)"
                        hint="Použije se, když zákazník opravu nevybral nebo oprava nemá u zařízení vyplněnou časovou náročnost."
                        value={settings.defaultRepairMinutes}
                        onChange={(value) => update({defaultRepairMinutes: value})}
                        min={5}
                        step={5}
                    />
                </div>
            </Card>

            <Card title="E-maily zákazníkům">
                <label className="mb-4 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={Boolean(settings.reminderEnabled)} onChange={(event) => update({reminderEnabled: event.target.checked})}/>
                    Den před potvrzeným termínem poslat připomínku od
                    <input type="number" min={0} max={23} value={settings.reminderHour} onChange={(event) => update({reminderHour: event.target.value})} className={`${inputClass} w-16`} disabled={!settings.reminderEnabled}/>
                    hodin
                </label>
                <p className="mb-2 text-sm text-gray-600">Kontakty v patičce e-mailů (potvrzení, připomínky, odpovědi):</p>
                <div className="grid gap-3 sm:grid-cols-2">
                    <TextField label="Telefon" value={settings.contact.phone} onChange={(value) => update({contact: {...settings.contact, phone: value}})}/>
                    <TextField label="E-mail" value={settings.contact.email} onChange={(value) => update({contact: {...settings.contact, email: value}})}/>
                    <TextField label="Adresa provozovny" value={settings.contact.address} onChange={(value) => update({contact: {...settings.contact, address: value}})} placeholder="Ulice 123, 530 02 Pardubice"/>
                    <TextField label="Odkaz na mapu" value={settings.contact.mapUrl} onChange={(value) => update({contact: {...settings.contact, mapUrl: value}})} placeholder="https://mapy.cz/…"/>
                </div>
            </Card>

            <Card
                title="Žádost o recenzi"
                description="Pár dní po dokončení opravy pošleme zákazníkovi e-mail s prosbou o hodnocení. Jen u oprav dokončených po zapnutí, zákazníkovi nejvýš jednou za půl roku, e-maily odcházejí mezi 9. a 18. hodinou."
            >
                <label className="mb-3 flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={Boolean(settings.reviewRequest?.enabled)}
                        onChange={(event) => update({
                            reviewRequest: {
                                ...settings.reviewRequest,
                                enabled: event.target.checked,
                                // počítá se od zapnutí – staří zákazníci e-mail nedostanou
                                ...(event.target.checked ? {enabledAt: new Date().toISOString()} : {})
                            }
                        })}
                    />
                    Posílat žádost o recenzi
                </label>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                    <TextField
                        label="Odkaz na hodnocení (Google)"
                        value={settings.reviewRequest?.url}
                        placeholder="https://g.page/r/…/review"
                        onChange={(value) => update({reviewRequest: {...settings.reviewRequest, url: value}})}
                    />
                    <NumberField
                        label="Dní po dokončení"
                        value={settings.reviewRequest?.delayDays}
                        min={0}
                        max={60}
                        onChange={(value) => update({reviewRequest: {...settings.reviewRequest, delayDays: value}})}
                    />
                </div>
                {settings.reviewRequest?.enabled && !(settings.reviewRequest?.url || '').trim() ? (
                    <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        Dokud nevyplníte odkaz na hodnocení, e-maily se neposílají. Po doplnění odejdou i zákazníkům, jejichž oprava byla dokončena od zapnutí (nejvýš 30 dní zpětně).
                    </p>
                ) : null}
                <p className="mt-2 text-xs text-gray-500">
                    Odkaz najdete v profilu firmy na Googlu (Google Business Profile → „Požádat o recenze“).
                    {settings.reviewRequest?.enabled && settings.reviewRequest?.enabledAt ? ` Zapnuto od ${new Date(settings.reviewRequest.enabledAt).toLocaleDateString('cs-CZ')}.` : ''}
                </p>
            </Card>

            <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur md:left-64">
                <div className="mx-auto flex max-w-4xl items-center justify-end gap-3">
                    {dirty ? <span className="text-sm text-amber-700">Neuložené změny</span> : null}
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !dirty}
                        className="rounded-md bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {saving ? 'Ukládám…' : 'Uložit nastavení'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const Card = ({title, description, children}) => (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-base font-semibold">{title}</h2>
        {description ? <p className="mb-3 mt-0.5 text-sm text-gray-600">{description}</p> : <div className="mb-3"/>}
        {children}
    </section>
);

const NumberField = ({label, hint, value, onChange, ...props}) => (
    <label className="block text-sm">
        <span className="font-medium">{label}</span>
        <input type="number" value={value ?? ''} onChange={(event) => onChange(event.target.value)} className={`${inputClass} mt-1 block w-32`} {...props}/>
        {hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
    </label>
);

const TextField = ({label, value, onChange, placeholder}) => (
    <label className="block text-sm">
        <span className="font-medium">{label}</span>
        <input value={value || ''} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} className={`${inputClass} mt-1 block w-full`}/>
    </label>
);

export default WebSettings;
